import { useState, type FormEvent } from 'react';
import Reveal from '@/components/apple/Reveal';
import { WHATSAPP_NUMBER } from '@/config/site';
import { Container, SectionTitle } from '@/components/apple/ui';

export const PERGUNTAS = [
  {
    q: 'Vocês atendem somente empresas com CNPJ?',
    a: 'O atendimento é prioritariamente corporativo. Pessoas sem CNPJ também podem ser atendidas quando a compra estiver relacionada a uma atividade profissional.',
  },
  {
    q: 'Preciso contratar um projeto completo?',
    a: 'Não. É possível comprar apenas cadeiras ou mesas, ou contratar o ambiente completo, com layout e implantação.',
  },
  {
    q: 'Vocês fazem o projeto do ambiente?',
    a: 'Sim. Desenvolvemos o layout do mobiliário ou trabalhamos com base no projeto do seu arquiteto.',
  },
  {
    q: 'Vocês atendem fora de Caruaru?',
    a: 'Sim. Atendemos Pernambuco e já entregamos projetos em vários estados do Nordeste. Outras localidades são avaliadas conforme porte e logística.',
  },
  {
    q: 'A entrega e a montagem estão incluídas?',
    a: 'As condições dependem da cidade, do volume e do escopo, e são informadas com clareza na proposta.',
  },
  {
    q: 'Em quanto tempo recebo a proposta?',
    a: 'Normalmente em até 3 dias úteis. Demandas simples podem ser respondidas no mesmo dia.',
  },
];

export function Perguntas() {
  const [aberto, setAberto] = useState<number | null>(null);
  return (
    <section className="bg-[#F5F5F7] py-24 md:py-36">
      <Container>
        <Reveal>
          <SectionTitle title="Perguntas frequentes." />
        </Reveal>
        <div className="mt-10 md:mt-14 max-w-3xl mx-auto border-t border-black/[0.1]">
          {PERGUNTAS.map((p, i) => {
            const open = aberto === i;
            return (
              <div key={p.q} className="border-b border-black/[0.1]">
                <button
                  type="button"
                  onClick={() => setAberto(open ? null : i)}
                  aria-expanded={open}
                  className="w-full flex items-center justify-between gap-6 py-5 md:py-6 text-left"
                >
                  <span className="text-[17px] md:text-[21px] font-semibold tracking-[-0.02em] text-[#1D1D1F]">{p.q}</span>
                  <span
                    aria-hidden="true"
                    className={`shrink-0 text-[24px] leading-none font-light text-[#6E6E73] transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
                  >
                    +
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                >
                  <div className="overflow-hidden">
                    <p className="pb-6 pr-10 text-[15px] md:text-[17px] leading-[1.55] text-[#6E6E73]">{p.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

const OPCOES = [
  'Cadeiras corporativas',
  'Mobiliar um escritório',
  'Mobiliar uma clínica',
  'Projeto completo (turnkey)',
  'Projeto de arquiteto',
  'Escola, igreja ou auditório',
  'Órgão público',
  'Visita ao showroom',
  'Outro',
];

const campo =
  'w-full bg-[#F5F5F7] rounded-[12px] border border-black/[0.14] px-4 py-3.5 text-[17px] text-[#1D1D1F] placeholder:text-[#6E6E73] focus:outline-none focus:border-[#E8610A] focus:ring-4 focus:ring-[#E8610A]/15';

export function Contato() {
  const [form, setForm] = useState({ nome: '', whatsapp: '', empresa: '', cidade: '', procura: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const linhas = [
      'Olá Ednilson! Vim pelo site da HomeOffice Premium.',
      `Nome: ${form.nome}`,
      form.whatsapp && `WhatsApp: ${form.whatsapp}`,
      form.empresa && `Empresa/CNPJ: ${form.empresa}`,
      form.cidade && `Cidade: ${form.cidade}`,
      form.procura && `Procuro: ${form.procura}`,
    ].filter(Boolean);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(linhas.join('\n'))}`, '_blank', 'noopener');
  };

  return (
    <section id="contato" className="bg-white py-24 md:py-36">
      <Container>
        <Reveal>
          <SectionTitle
            title="Conte o que sua empresa precisa."
            sub="O proprietário analisa pessoalmente. Você continua a conversa direto no WhatsApp."
          />
        </Reveal>

        <Reveal delay={1}>
          <form onSubmit={enviar} className="mt-10 md:mt-14 max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-readdy-form="contato-home">
            <label className="sm:col-span-2">
              <span className="sr-only">Nome</span>
              <input required value={form.nome} onChange={set('nome')} placeholder="Nome" autoComplete="name" className={campo} />
            </label>
            <label>
              <span className="sr-only">WhatsApp</span>
              <input value={form.whatsapp} onChange={set('whatsapp')} placeholder="WhatsApp" inputMode="tel" autoComplete="tel" className={campo} />
            </label>
            <label>
              <span className="sr-only">Cidade</span>
              <input value={form.cidade} onChange={set('cidade')} placeholder="Cidade" autoComplete="address-level2" className={campo} />
            </label>
            <label className="sm:col-span-2">
              <span className="sr-only">Empresa ou CNPJ</span>
              <input value={form.empresa} onChange={set('empresa')} placeholder="Empresa ou CNPJ" autoComplete="organization" className={campo} />
            </label>
            <label className="sm:col-span-2">
              <span className="sr-only">O que procura?</span>
              <select value={form.procura} onChange={set('procura')} className={`${campo} appearance-none ${form.procura ? '' : 'text-[#6E6E73]'}`}>
                <option value="">O que procura?</option>
                {OPCOES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <div className="sm:col-span-2 mt-4 text-center">
              <button
                type="submit"
                className="inline-flex items-center justify-center bg-[#E8610A] text-white text-[17px] px-8 py-3.5 rounded-full hover:bg-[#C4500A]"
              >
                Continuar no WhatsApp
              </button>
              <p className="mt-3 text-[13px] text-[#6E6E73]">Retorno assim que a mensagem for visualizada.</p>
            </div>
          </form>
        </Reveal>
      </Container>
    </section>
  );
}
